import AsyncStorage from '../Storage/Async/AsyncStorage';
import { EYE_CLOSED_ICON_DATA_URI } from './eyeClosedIcon';
import { formatTimerOffMessage } from './mutedMessage';

const OVERLAY_SHOWN_KEY = 'Screen.Overlay.Shown';
const APPLET_MUTED_OVERLAY_ID = 'applet-muted-overlay';
const APPLET_MUTED_OVERLAY_TEXT_ID = 'applet-muted-overlay-text';
const APPLET_MUTED_BLUR = 'blur(8px)';

// The elements that hold the visible playback: the applet iframe container and the two video
// wrappers (same set the screen-rotation manager transforms, see defaultDriverFactory.ts).
const MUTED_CONTENT_ELEMENT_IDS = ['body', 'videos-wrapper-foreground', 'videos-wrapper-background'];

function getAppletMutedOverlayText(nextPowerOn: Date | null): string {
	return formatTimerOffMessage(nextPowerOn, { playbackRunning: true });
}

// We blur the playback elements directly with `filter` rather than blurring through the overlay with
// `backdrop-filter`: WebOS' Chromium reports `backdrop-filter` as supported (CSS.supports returns true)
// but never paints it — it does not sample the applet's separate compositor layer, so the overlay shows
// no blur at all. A plain `filter: blur()` on the element is painted reliably on every supported platform.
function setMutedContentBlur(window: Window, blur: string) {
	for (const id of MUTED_CONTENT_ELEMENT_IDS) {
		const element = window.document.getElementById(id);
		if (!element) {
			continue;
		}
		if (blur) {
			element.style.filter = blur;
			element.style.setProperty('-webkit-filter', blur);
		} else {
			element.style.removeProperty('filter');
			element.style.removeProperty('-webkit-filter');
		}
	}
}

export async function isOverlayShown(storage: AsyncStorage) {
	return (await storage.getItem(OVERLAY_SHOWN_KEY)) === '1';
}

export async function hideOverlay(window: Window, storage: AsyncStorage) {
	await storage.removeItem(OVERLAY_SHOWN_KEY);
	await updateOverlay(window, storage);
}

export async function showOverlay(window: Window, storage: AsyncStorage) {
	await storage.setItem(OVERLAY_SHOWN_KEY, '1');
	await updateOverlay(window, storage);
}

export function showAppletMutedOverlay(window: Window, nextPowerOn: Date | null = null) {
	setMutedContentBlur(window, APPLET_MUTED_BLUR);
	const existing = window.document.getElementById(APPLET_MUTED_OVERLAY_ID);
	if (existing) {
		const existingText = window.document.getElementById(APPLET_MUTED_OVERLAY_TEXT_ID);
		if (existingText) {
			existingText.textContent = getAppletMutedOverlayText(nextPowerOn);
		}
		return;
	}
	const overlay = window.document.createElement('div');
	overlay.setAttribute('id', APPLET_MUTED_OVERLAY_ID);
	overlay.style.position = 'fixed';
	overlay.style.left = '0';
	overlay.style.top = '0';
	overlay.style.width = '100%';
	overlay.style.height = '100%';
	overlay.style.zIndex = '99998';
	overlay.style.background = 'linear-gradient(to bottom, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0) 100%)';
	overlay.style.display = 'flex';
	overlay.style.alignItems = 'center';
	overlay.style.justifyContent = 'center';
	overlay.style.color = '#ffffff';
	overlay.style.fontFamily = 'sans-serif';
	overlay.style.fontSize = '2rem';
	overlay.style.textAlign = 'center';
	overlay.style.padding = '1rem';
	overlay.style.textShadow = '0 2px 8px rgba(0, 0, 0, 0.6)';

	const icon = window.document.createElement('img');
	icon.setAttribute('src', EYE_CLOSED_ICON_DATA_URI);
	icon.setAttribute('alt', '');
	icon.setAttribute('role', 'presentation');
	icon.style.position = 'fixed';
	icon.style.top = '0';
	icon.style.right = '0';
	icon.style.marginTop = '2em';
	icon.style.marginRight = '2em';
	icon.style.width = '200px';
	icon.style.height = 'auto';
	overlay.appendChild(icon);

	const text = window.document.createElement('div');
	text.setAttribute('id', APPLET_MUTED_OVERLAY_TEXT_ID);
	text.textContent = getAppletMutedOverlayText(nextPowerOn);
	overlay.appendChild(text);

	window.document.body.appendChild(overlay);
}

export function hideAppletMutedOverlay(window: Window) {
	setMutedContentBlur(window, '');
	const overlay = window.document.getElementById(APPLET_MUTED_OVERLAY_ID);
	if (overlay && overlay.parentElement) {
		overlay.parentElement.removeChild(overlay);
	}
}

export async function updateOverlay(window: Window, storage: AsyncStorage) {
	const overlayShown = await storage.getItem(OVERLAY_SHOWN_KEY);
	const overlayId = 'screen-overlay';
	if (!overlayShown) {
		const overlay = window.document.getElementById(overlayId);
		if (overlay && overlay.parentElement) {
			overlay.parentElement.removeChild(overlay);
		}
	} else {
		const existingOverlay = window.document.getElementById(overlayId);
		if (!existingOverlay) {
			const overlay = window.document.createElement('div');
			overlay.setAttribute('id', overlayId);
			overlay.style.backgroundColor = '#000000';
			overlay.style.position = 'absolute';
			overlay.style.left = '0';
			overlay.style.top = '0';
			overlay.style.width = '100%';
			overlay.style.height = '100%';
			overlay.style.zIndex = '99999';
			window.document.body.appendChild(overlay);
		}
	}
}
